import { getAuthenticationToken } from "../../auth/helpers";
import { buildConfigForEnvironment } from "../config";
import { GetFeatureFlagsResponse, FeatureFlagName } from "./types";

// Set VITE_USE_MOCK_FEATURE_FLAGS=true in .env.local to use mock flags locally.
// Gated on DEV because Vite loads .env.local in every mode, including `vite build`.
// Without this, a manual deploy from a workspace that has the var set ships a bundle
// with the flag values frozen in and the AppConfig fetch tree-shaken away entirely.
const USE_MOCK_FLAGS =
  import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_FEATURE_FLAGS === "true";

const getMockFlags = (): Record<string, boolean> => ({
  enableIplClaims: import.meta.env.VITE_FEATURE_FLAG_ENABLE_IPL_CLAIMS === "true",
  enableWcCanadaClaims: import.meta.env.VITE_FEATURE_FLAG_ENABLE_WC_CANADA_CLAIMS === "true",
  enableCargoJsonForms: import.meta.env.VITE_FEATURE_FLAG_ENABLE_CARGO_JSON_FORMS === "true",
  enableJsonForms: import.meta.env.VITE_FEATURE_FLAG_ENABLE_JSON_FORMS === "true",
  enableFormStudio: import.meta.env.VITE_FEATURE_FLAG_ENABLE_FORM_STUDIO === "true",
  enableConfigDrivenClaimsHistory:
    import.meta.env.VITE_FEATURE_FLAG_ENABLE_CONFIG_DRIVEN_CLAIMS_HISTORY === "true",
});

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// A cold backend (Lambda cold start plus the AppConfig round trip) regularly
// outlived the previous single 3s attempt. Because a failure here leaves the
// cache empty and every consumer then reads false, one slow response presented
// as "every feature is disabled". Retry transient failures instead.
const FETCH_TIMEOUT_MS = 4000; // per attempt
const MAX_FETCH_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 150;

// index.tsx blocks the first render on this call, so the retries need a hard
// ceiling: a flags outage must not turn into a long blank screen.
const TOTAL_FETCH_BUDGET_MS = 8000;

interface CachedFlags {
  flags: Record<string, boolean>;
  timestamp: number;
}

/** Carries the HTTP status so retry classification can distinguish 5xx from 4xx. */
class FeatureFlagsHttpError extends Error {
  constructor(readonly status: number) {
    super(`Failed to fetch feature flags: ${status}`);
    this.name = "FeatureFlagsHttpError";
  }
}

/** A 4xx other than 429 is a decision, not a blip, so retrying cannot change it. */
const isRetryable = (error: unknown): boolean => {
  if (error instanceof FeatureFlagsHttpError) {
    return error.status === 429 || error.status >= 500;
  }
  // Timeout aborts and network failures.
  return true;
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

let cachedFlags: CachedFlags | null = null;
let inFlightRequest: Promise<Record<string, boolean>> | null = null;

const isCacheValid = (): boolean => {
  if (!cachedFlags) return false;
  return Date.now() - cachedFlags.timestamp < CACHE_TTL_MS;
};

export const getCachedFlags = (): Record<string, boolean> | null => {
  return cachedFlags?.flags ?? null;
};

/**
 * Returns true once feature flags have been successfully loaded into the
 * module-scoped cache (either from the network or from mock flags).
 *
 * Use this to distinguish "flags failed / haven't finished loading" from
 * "flag is explicitly false". Without this check, consumers reading flags
 * via getCachedFlags()/FeatureFlags would silently fall back to false
 * during the loading window or after a fetch failure.
 */
export const areFeatureFlagsLoaded = (): boolean => cachedFlags !== null;

// TODO: Replace with Smithy client once GetFeatureFlagsCommand is available
export const fetchFeatureFlags = async (): Promise<Record<string, boolean>> => {
  if (isCacheValid() && cachedFlags) {
    return cachedFlags.flags;
  }

  // Deduplicate concurrent requests
  if (inFlightRequest) {
    return inFlightRequest;
  }

  inFlightRequest = doFetchFeatureFlags();
  try {
    return await inFlightRequest;
  } finally {
    inFlightRequest = null;
  }
};

const doFetchFeatureFlags = async (): Promise<Record<string, boolean>> => {
  if (USE_MOCK_FLAGS) {
    const flags = getMockFlags();
    cachedFlags = { flags, timestamp: Date.now() };
    return flags;
  }

  const token = await getAuthenticationToken();
  if (!token) {
    if (cachedFlags?.flags) {
      return cachedFlags.flags;
    }
    throw new Error("No authentication token available");
  }

  const config = buildConfigForEnvironment();
  const endpoint = `${config.endpoint}/getFeatureFlags`;
  const deadline = Date.now() + TOTAL_FETCH_BUDGET_MS;

  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt++) {
    try {
      const flags = await attemptFetchFlags(endpoint, token, deadline);
      cachedFlags = { flags, timestamp: Date.now() };
      return flags;
    } catch (error) {
      lastError = error;
      console.error("Error fetching feature flags:", error);

      // A stale value beats waiting longer for a fresh one: consumers can only
      // tell "never loaded" apart from "loaded as false", not stale from fresh.
      if (cachedFlags?.flags) {
        return cachedFlags.flags;
      }

      if (!isRetryable(error) || attempt === MAX_FETCH_ATTEMPTS) {
        break;
      }

      const delay = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
      if (Date.now() + delay >= deadline) {
        break;
      }
      await sleep(delay);
    }
  }

  // Re-throw so consumers can distinguish an outage from a disabled feature.
  throw lastError;
};

const attemptFetchFlags = async (
  endpoint: string,
  token: string,
  deadline: number
): Promise<Record<string, boolean>> => {
  const controller = new AbortController();
  const remainingBudget = deadline - Date.now();
  const timeoutId = setTimeout(
    () => controller.abort(),
    Math.max(0, Math.min(FETCH_TIMEOUT_MS, remainingBudget))
  );

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new FeatureFlagsHttpError(response.status);
    }

    const data: GetFeatureFlagsResponse = await response.json();
    return data.flags ?? {};
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getFeatureFlag = async (
  flagName: FeatureFlagName,
  defaultValue = false
): Promise<boolean> => {
  try {
    const flags = await fetchFeatureFlags();
    return flags[flagName] ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

/** Thrown when a flag value could not be established because the lookup failed. */
export class FeatureFlagsUnavailableError extends Error {
  constructor(flagName: FeatureFlagName) {
    super(`Feature flags unavailable; cannot resolve "${flagName}"`);
    this.name = "FeatureFlagsUnavailableError";
  }
}

/**
 * Resolves a flag, distinguishing "explicitly off" from "lookup failed".
 *
 * getFeatureFlag collapses both into false, which makes a failing flags
 * request indistinguishable from a disabled feature. Callers that turn a
 * false into a user-facing denial need to tell those apart, otherwise an
 * outage is reported to the user as a permissions problem.
 *
 * @throws {FeatureFlagsUnavailableError} when no flag values could be loaded.
 */
export const requireFeatureFlag = async (flagName: FeatureFlagName): Promise<boolean> => {
  const enabled = await getFeatureFlag(flagName);

  // A stale cache still counts as loaded: doFetchFeatureFlags falls back to it
  // rather than throwing, so the value is known even if the last refresh failed.
  if (!enabled && !areFeatureFlagsLoaded()) {
    throw new FeatureFlagsUnavailableError(flagName);
  }

  return enabled;
};

export const clearFeatureFlagsCache = (): void => {
  cachedFlags = null;
  inFlightRequest = null;
};
